// src/components/PublicAthletes.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './TableList.css';
import './PublicAthletes.css';

export default function PublicAthletes() {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('status-active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentYear = new Date().getFullYear();

  const years = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1],
    [currentYear]
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';
  const isActive = (a) => !!(a && toBool(a.is_legitimated));
  const norm = (s) =>
    (s ?? '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');

  const fetchAthletes = async (clubName = '', year = selectedYear) => {
    setLoading(true);
    setError(null);
    try {
      let url = `https://www.fro.ro/api/athletes/show-athletes.php?year=${year}`;
      if (clubName) url += `&club=${encodeURIComponent(clubName)}`;
      const res = await fetch(url, { cache: 'no-store' });
      const data = await res.json();

      if (data.success) {
        const list = data.athletes || [];
        setAthletes(list);
        if (!clubName) {
          const uniqueClubs = [...new Set(list.map(a => a.club_name).filter(Boolean))];
          uniqueClubs.sort((a, b) => a.localeCompare(b, 'ro'));
          setClubs(uniqueClubs);
        }
      } else {
        setAthletes([]);
        setError(data.message || 'Eroare la încărcarea sportivilor');
      }
    } catch {
      setError('Eroare la conexiune');
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAthletes('', selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const handleClubChange = (e) => {
    const club = e.target.value;
    setSelectedClub(club);
    fetchAthletes(club, selectedYear);
  };

  const handleYearChange = (e) => setSelectedYear(Number(e.target.value));

  const maskCNP = (cnp) => {
    if (cnp === null || cnp === undefined) return '-';
    const s = String(cnp).trim();
    if (!s) return '-';
    if (s.length < 3) return s;
    return s.substring(0, 3) + '*********';
  };

  const query = norm(search);
  const filteredAthletes = athletes.filter((a) => {
    const fullNameNorm = norm(`${a.first_name || ''} ${a.last_name || ''}`.trim());

    const birthStr = String(a.birth_year || '');
    const matchesSearch =
      query === '' || fullNameNorm.includes(query) || birthStr.includes(search.trim());
    const matchesClub = selectedClub === '' || a.club_name === selectedClub;
    return matchesSearch && matchesClub;
  });

  const sortedAthletes = [...filteredAthletes].sort((a, b) => {
    const aActive = isActive(a) ? 1 : 0;
    const bActive = isActive(b) ? 1 : 0;

    switch (sortBy) {
      case 'birth_year-asc':
        return (a.birth_year || 0) - (b.birth_year || 0);
      case 'birth_year-desc':
        return (b.birth_year || 0) - (a.birth_year || 0);
      case 'status-active':
        return bActive - aActive;
      case 'status-inactive':
        return aActive - bActive;
      case 'name-az':
      default: {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();

        return nameA.localeCompare(nameB, 'ro');
      }
    }
  });

  const sublineStyle = { display: 'block', marginTop: 4, fontSize: 12, color: '#666' };

  return (
    <>
      <div className="athletes-container">
        {/* 🔹 Buton Înapoi */}
        <button onClick={() => navigate('/')} className="back-button">
          <ArrowLeft size={18} strokeWidth={2.2} />
        </button>
        <h1 className="title">Lista sportivilor</h1>
      </div>

      <div className="table-list-container">
        <div className="table-list-header">
          <div className="sort-bar">
            <label htmlFor="sort-select" className="sort-label">Sortare:</label>
            <select
              id="sort-select"
              className="sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="name-az">A–Z după nume</option>
              <option value="birth_year-asc">An naștere ↑</option>
              <option value="birth_year-desc">An naștere ↓</option>
              <option value="status-active">Activi întâi</option>
              <option value="status-inactive">Inactivi întâi</option>
            </select>
          </div>
          <div className="sort-bar" style={{ width: '20%' }}>
            <label htmlFor="club-select" className="sort-label">
              Filtrează după club:
            </label>

            <select
              id="club-select"
              className="sort-select"
              value={selectedClub}
              onChange={handleClubChange}
            >
              <option value="">Toate cluburile</option>
              {clubs.map((club, idx) => (
                <option key={idx} value={club}>
                  {club}
                </option>
              ))}
            </select>
          </div>

          <div className="sort-bar">
            <label htmlFor="year-select" className="sort-label">An:</label>
            <select
              id="year-select"
              className="sort-select"
              value={selectedYear}
              onChange={handleYearChange}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y === currentYear ? `${y} (curent)` : `${y}`}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
            <input
              type="text"
              className="search-bar"
              placeholder="Caută după nume sau an naștere (ex: 2008)"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <table className="table-list">
          <thead>
            <tr>
              <th>Nr. legitimație</th>
              <th>Nume</th>
              <th>An naștere</th>
              <th>CNP</th>
              <th>Club</th>
              <th>Activ</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>
                  Se încarcă sportivii...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'crimson' }}>
                  {error}
                </td>
              </tr>
            ) : sortedAthletes.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>
                  Nu există sportivi pentru filtrul selectat.
                </td>
              </tr>
            ) : (
              sortedAthletes.map((a, idx) => {
                const statusText = isActive(a) ? 'Activ' : 'Inactiv';
                return (
                  <tr key={idx}>
                    <td>
                      {a.nr_legitimatie && a.nr_legitimatie.trim() !== '' ? (
                        <span className="badge-legitimatie">{a.nr_legitimatie}</span>
                      ) : (
                        <span className="badge-legitimatie empty">-</span>
                      )}
                    </td>
                    <td>
                      <span>{a.first_name} {a.last_name}</span>
                      <span style={sublineStyle}>{statusText}</span>
                    </td>
                    <td>{a.birth_year || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{maskCNP(a.cnp)}</span>

                        {toBool(a.is_foreign_cnp) && a.foreign_country && (
                          <span
                            className="badge-foreign"
                            title={`Sportiv străin (${a.foreign_country})`}
                          >
                            STRĂIN · {a.foreign_country}
                          </span>
                        )}
                      </div>
                    </td>

                    <td>{a.club_name || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {isActive(a) ? (
                        <span className="leg-chip leg-chip--on" aria-label="Activ" title="Activ">
                          <svg width="14" height="14" viewBox="0 0 24 24">
                            <rect x="1.5" y="1.5" width="21" height="21" rx="4" fill="#111" />
                            <path d="M6 12.5l4 4 8-9" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : (
                        <span className="leg-chip leg-chip--off" aria-label="Inactiv" title="Inactiv">
                          <svg width="14" height="14" viewBox="0 0 24 24">
                            <rect x="1.5" y="1.5" width="21" height="21" rx="4" fill="#f1f1f1" stroke="#ccc" />
                          </svg>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
